import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { SiteVisibility } from '@gitbook/api-client';

import { apiQuery } from '../../state';
import { siteVisitorAuthenticatedSelector, siteVisitorSegmentSelector } from './state';

/**
 * Whether the visitor previewed for a site is signed in.
 *
 * Every visitor of a site is signed in when it authenticates them before serving any content, and
 * so is any visitor carrying claims, which only a verified token delivers. The stored choice is
 * what tells the remaining two apart: an anonymous visitor and a signed-in one carrying no claim.
 */
export function useSiteVisitorIsAuthenticated(args: { organizationId: string; siteId: string }) {
    const { organizationId, siteId } = args;
    const [chosen, setAuthenticated] = useAtom(siteVisitorAuthenticatedSelector(siteId));
    const [visitorSegment] = useAtom(siteVisitorSegmentSelector(siteId));

    const { data: site } = useQuery(
        apiQuery('getSiteById', { organizationId, siteId }, { nullOnError: true })
    );
    const alwaysAuthenticated = site?.visibility === SiteVisibility.VisitorAuth;

    return {
        isAuthenticated: alwaysAuthenticated || visitorSegment !== null || chosen,
        alwaysAuthenticated,
        setAuthenticated,
    };
}
